import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { getCompositeKey } from 'shared/utils/compositeKey';
import {
  type Cart,
  type CartItem,
  VeevaResponse,
  VeevaResponsePayloadError,
  VeevaResponsePayloadSuccess,
  VeevaResponseStatus,
} from './types';
import type { Batch } from '../../batch/model/types';

export const veevaResponseStatusMap: Record<Lowercase<VeevaResponseStatus>, VeevaResponseStatus> = {
  success: 'SUCCESS',
  failure: 'FAILURE',
};

type CartSliceState = {
  isLoading: boolean,
  errors: VeevaResponsePayloadError[],
  success: VeevaResponsePayloadSuccess[]
} & Cart

const initialState: CartSliceState = {
  items: {},
  isLoading: false,
  errors: [],
  success: [],
};

function createCartItem(item: Batch): CartItem {
  return {
    ...item,
  };
}

export const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    clearSuccess: (state) => {
      state.success = [];
    },
    clearCart: (state) => {
      state.items = {};
      state.success = [];
      state.errors = [];
    },
    addErrors: (state, action:PayloadAction<VeevaResponse>) => {
      state.errors = (action.payload?.data || [])
        .filter((item) =>
          item.responseStatus === veevaResponseStatusMap.failure)
        .map((el) => el.data) as VeevaResponsePayloadError[];
    },
    clearErrors: (state) => {
      state.errors = [];
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    addSuccess: (state, action: PayloadAction<VeevaResponse>) => {
      state.success = [Math.random()] as any;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    addNullItem: (state, action: PayloadAction<Batch>) => {
      const key = getCompositeKey(action.payload);
      state.items[key] = {
        ...createCartItem(action.payload),
        batchId: null as unknown as string,
      };
    },
    addItem: (state, action: PayloadAction<Batch>) => {
      // batchId is not unique, if the item with the same id is added
      // the item in the cart would be overwritten!!!
      // in this way we guarantee that the item with the same batchId
      // would never be added to cart
      const key = getCompositeKey(action.payload);
      state.items[key] = createCartItem(action.payload);
    },
    removeItem: (state, action: PayloadAction<Batch>) => {
      const key = getCompositeKey(action.payload);
      if (state.items[key]) {
        // @ts-ignore
        state.items[key] = undefined;
        delete state.items[key];
      }
      state.errors = [];
    },
  },
});

export const selectProductsInCart = (state: RootState) => state.cart.items;
export const isInTheCart = (batchId: Batch['batchId']) => (state: RootState) => !!state.cart.items[batchId];
export const isCartLoading = (state: RootState) => state.cart.isLoading;
export const selectCartErrors = (state: RootState) => state.cart.errors;
export const selectCartSuccess = (state: RootState) => state.cart.success;
export const getItemInCartByCompositeKey = (batch: Batch) =>
  (state: RootState) => {
    const key = getCompositeKey(batch);
    return state.cart.items[key];
  };

export const {
  addItem,
  removeItem,
  clearCart,
  addSuccess,
  setLoading,
  addErrors,
  clearSuccess,
  clearErrors,
  addNullItem,
} = cartSlice.actions;
